import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, GripVertical, Trash2, MapPin,
  Pencil, Check, X, Loader2, CalendarDays, DoorOpen, Flag, Save, Undo2, StickyNote,
  MoreVertical, Layers, CalendarRange, Users, Eye, History,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { frenchDate, frenchDateShort } from "@/lib/shariti";
import { ZoneMapDialog } from "@/components/ZoneMapDialog";
import { TeamSheet } from "@/components/TeamSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toISODate(d);
}
function mondayOf(date) {
  const d = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return toISODate(d);
}

// Palette de pastilles pour le chef d'équipe (équivalent CDP).
const CHEF_COLORS = ["#EE722E", "#7C3AED", "#2F6FED", "#1F9D55", "#E9B800", "#C026D3", "#0D9488", "#D64545"];

// Indicateur visuel léger du statut d'une cellule (ville).
const PAP_STATUS = {
  prevu: { label: "Prévu", dot: "#6B7280", cell: "border-[var(--border-soft)] bg-white", text: "text-ink" },
  confirme: { label: "Confirmé", dot: "#1F9D55", cell: "border-[rgba(31,157,85,0.45)] bg-[rgba(31,157,85,0.08)]", text: "text-[#0F5A2F]" },
  annule: { label: "Annulé", dot: "#D64545", cell: "border-[rgba(214,69,69,0.45)] bg-[rgba(214,69,69,0.06)]", text: "text-[#9B2226] line-through" },
};
const STATUS_OPTIONS = [
  { v: "prevu", l: "Prévu" },
  { v: "confirme", l: "Confirmé" },
  { v: "annule", l: "Annulé" },
];
const ZONE_OPTIONS = [
  { v: "none", l: "Aucune zone" },
  { v: "1", l: "Zone 1" },
  { v: "2", l: "Zone 2" },
  { v: "3", l: "Zone 3" },
  { v: "4", l: "Zone 4" },
];

/** Formate un horodatage ISO (UTC) en date + heure françaises lisibles. */
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return (
      d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) +
      " à " +
      d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return iso;
  }
}

/** Éditeur d'une cellule PAP : ville + zone + statut + note + extension multi-jours. */
// Détail « bulletins par recruteur » d'une cellule PAP (ville + jour) — managers only.
// Monté à l'ouverture du popover d'édition : il charge et affiche la répartition,
// avec le MÊME comptage que le badge (bulletins valides, ville normalisée).
function PapBulletinsBreakdown({ ville, date, count }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/pap/bulletins-detail", { params: { ville, date } });
        if (alive) setDetail(data);
      } catch { if (alive) setDetail({ recruteurs: [], total: count }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [ville, date]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mb-2.5 overflow-hidden rounded-lg border border-[var(--border-soft)]" data-testid="pap-bulletins-breakdown">
      <div className="flex items-center gap-2 bg-[#FBF7F2] px-2.5 py-1.5">
        <Users className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
        <span className="text-[11px] font-bold text-ink">Bulletins par recruteur</span>
        <span className="ml-auto grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white"
          style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
          {detail?.total ?? count}
        </span>
      </div>
      <div className="max-h-40 overflow-y-auto p-1">
        {loading ? (
          <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-muted-ink">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
          </div>
        ) : (detail?.recruteurs?.length ? detail.recruteurs.map((r) => (
          <div key={r.rd_id || r.code || r.name}
            className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-black/5"
            data-testid={`pap-bulletins-rd-${r.rd_id || r.code}`}>
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[9px] font-bold text-[var(--shariti-orange)]"
              style={{ background: "rgba(238,114,46,0.14)" }}>
              {(r.name || "?").trim().slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-ink" title={r.name}>{r.name}</span>
            <span className="shrink-0 rounded-full bg-black/5 px-1.5 text-[10px] font-bold tabular-nums text-ink">{r.count}</span>
          </div>
        )) : (
          <div className="px-2 py-2 text-[11px] text-muted-ink">Aucun détail disponible.</div>
        ))}
      </div>
    </div>
  );
}


function PapCellEditor({ ligne, date, cell, days, villes = [], onSave, onClear, onReload, onTeam, teamCount = 0, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [ville, setVille] = useState(cell?.ville || "");
  const [zone, setZone] = useState(cell?.zone || "none");
  const [status, setStatus] = useState(cell?.status || "prevu");
  const [notes, setNotes] = useState(cell?.notes || "");
  const [selectedDays, setSelectedDays] = useState([date]);
  const [saving, setSaving] = useState(false);
  const [villeBoxOpen, setVilleBoxOpen] = useState(false);
  const [apiVilles, setApiVilles] = useState([]); // suggestions geo.api.gouv.fr (MAJUSCULES)

  // Autocomplétion des communes françaises (API officielle, debounce 250ms).
  useEffect(() => {
    if (!villeBoxOpen) return undefined;
    const q = ville.trim();
    if (q.length < 2) { setApiVilles([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/pap/city-autocomplete", { params: { q } });
        if (!cancelled) setApiVilles(data.villes || []);
      } catch (_) { if (!cancelled) setApiVilles([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ville, villeBoxOpen]);

  useEffect(() => {
    if (open) {
      setVille(cell?.ville || "");
      setZone(cell?.zone || "none");
      setStatus(cell?.status || "prevu");
      setNotes(cell?.notes || "");
      setSelectedDays([date]);
      setVilleBoxOpen(false);
    }
  }, [open]); // eslint-disable-line

  const toggleDay = (iso) =>
    setSelectedDays((cur) => (cur.includes(iso) ? cur.filter((d) => d !== iso) : [...cur, iso]));

  const submit = async () => {
    const dates = selectedDays.length ? selectedDays : [date];
    setSaving(true);
    try {
      await onSave({
        ligne_id: ligne.id, dates, ville: ville.trim().toUpperCase(),
        zone: zone === "none" ? null : zone, status, notes: notes.trim() || null,
      });
      setOpen(false);
    } finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try { await onClear(cell); setOpen(false); } finally { setSaving(false); }
  };

  const meta = cell ? (PAP_STATUS[cell.status] || PAP_STATUS.prevu) : null;
  // Nombre de bulletins de la ville ce jour-là (perf du PAP). Le backend ne fournit
  // ce champ qu'aux managers (admin / CDP / coordinateur PAP) — jamais le terrain.
  const bulletinsCount = Number(cell?.bulletins_jour) || 0;

  // Mode consultation (lecture seule) : affichage statique, aucune édition possible.
  if (readOnly) {
    return cell ? (
      <div data-testid={`pap-cell-${ligne.id}-${date}`}
        className={`flex h-full min-h-[60px] w-full flex-col justify-center gap-1 rounded-lg border px-2 py-1.5 text-left ${meta.cell}`}>
        <div className="flex items-start gap-1.5">
          <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full" style={{ background: meta.dot }} />
          <span className={`min-w-0 line-clamp-2 text-[13px] font-bold leading-tight ${meta.text}`} title={cell.ville}>{cell.ville}</span>
          {bulletinsCount > 0 && (
            <span data-testid={`pap-cell-bulletins-${ligne.id}-${date}`} title={`${bulletinsCount} bulletin${bulletinsCount > 1 ? "s" : ""} enregistré${bulletinsCount > 1 ? "s" : ""} ce jour`}
              className="ml-auto grid h-[19px] min-w-[19px] shrink-0 place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)] ring-2 ring-white/75"
              style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
              {bulletinsCount}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {cell.zone && (
            <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(238,114,46,0.14)] px-1 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">
              <Layers className="h-2.5 w-2.5" />Zone {cell.zone}
            </span>
          )}
          {cell.geo_zone && <MapPin className="h-2.5 w-2.5 text-[var(--shariti-orange)]" title="Zone terrain tracée sur carte" />}
          <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">{meta.label}</span>
          {cell.notes && <StickyNote className="h-2.5 w-2.5 text-muted-ink" title={cell.notes} />}
          {teamCount > 0 && (
            <span role="button" tabIndex={0}
              data-testid={`pap-cell-teamcount-${ligne.id}-${date}`}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(cell); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(cell); } }}
              title="Voir l'équipe du jour (personnes et horaires)"
              className="inline-flex cursor-pointer items-center gap-0.5 rounded bg-[var(--shariti-orange)] px-1.5 py-0.5 text-[9px] font-extrabold text-white transition-colors hover:bg-[#d9631f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
              <Users className="h-2.5 w-2.5" />{teamCount}
            </span>
          )}
        </div>
      </div>
    ) : (
      <div data-testid={`pap-empty-cell-${ligne.id}-${date}`}
        className="flex h-full min-h-[60px] w-full items-center justify-center rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink/40">
        <span className="text-[11px]">—</span>
      </div>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {cell ? (
          <button data-testid={`pap-cell-${ligne.id}-${date}`}
            className={`group flex h-full min-h-[60px] w-full flex-col justify-center gap-1 rounded-lg border px-2 py-1.5 text-left transition-colors hover:border-[var(--shariti-orange)] ${meta.cell}`}>
            <div className="flex items-start gap-1.5">
              <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full" style={{ background: meta.dot }} />
              <span className={`min-w-0 line-clamp-2 text-[13px] font-bold leading-tight ${meta.text}`} title={cell.ville}>{cell.ville}</span>
              {bulletinsCount > 0 && (
                <span data-testid={`pap-cell-bulletins-${ligne.id}-${date}`} title={`${bulletinsCount} bulletin${bulletinsCount > 1 ? "s" : ""} enregistré${bulletinsCount > 1 ? "s" : ""} ce jour`}
                  className="ml-auto grid h-[19px] min-w-[19px] shrink-0 place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)] ring-2 ring-white/75"
                  style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
                  {bulletinsCount}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {cell.zone && (
                <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(238,114,46,0.14)] px-1 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]" data-testid={`pap-cell-zone-${ligne.id}-${date}`}>
                  <Layers className="h-2.5 w-2.5" />Zone {cell.zone}
                </span>
              )}
              {cell.geo_zone && <MapPin className="h-2.5 w-2.5 text-[var(--shariti-orange)]" title="Zone terrain tracée sur carte" data-testid={`pap-cell-geozone-${ligne.id}-${date}`} />}
              <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">{meta.label}</span>
              {cell.notes && <StickyNote className="h-2.5 w-2.5 text-muted-ink" title={cell.notes} />}
              {teamCount > 0 && (
                <span role="button" tabIndex={0} data-testid={`pap-cell-teamcount-${ligne.id}-${date}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(cell); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(cell); } }}
                  title="Voir / composer l'équipe du jour (personnes et horaires)"
                  className="inline-flex cursor-pointer items-center gap-0.5 rounded bg-[var(--shariti-orange)] px-1.5 py-0.5 text-[9px] font-extrabold text-white transition-colors hover:bg-[#d9631f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
                  <Users className="h-2.5 w-2.5" />{teamCount}
                </span>
              )}
            </div>
          </button>
        ) : (
          <button data-testid={`pap-empty-cell-${ligne.id}-${date}`}
            className="group flex h-full min-h-[60px] w-full items-center justify-center rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] hover:text-[var(--shariti-orange)]">
            <Plus className="h-4 w-4" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" sideOffset={6} collisionPadding={12}
        className="w-80 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3"
        data-testid={`pap-cell-editor-${ligne.id}-${date}`}>
        <div className="mb-2 text-xs font-bold text-ink">
          {ligne.equipe_nom ? `${ligne.equipe_nom} · ` : ""}{ligne.name} · {frenchDate(date)}
        </div>
        {cell && bulletinsCount > 0 && (
          <PapBulletinsBreakdown ville={cell.ville} date={date} count={bulletinsCount} />
        )}
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px]">Ville</Label>
            <div className="relative">
              <Input value={ville} autoFocus
                onChange={(e) => { setVille(e.target.value); setVilleBoxOpen(true); }}
                onFocus={() => setVilleBoxOpen(true)}
                onBlur={() => setTimeout(() => setVilleBoxOpen(false), 150)}
                onKeyDown={(e) => { if (e.key === "Enter" && ville.trim()) { setVilleBoxOpen(false); submit(); } }}
                placeholder="Nom de la ville…" autoComplete="off" className="mt-1 h-9" data-testid={`pap-cell-ville-${ligne.id}-${date}`} />
              {villeBoxOpen && (() => {
                const q = ville.trim().toLowerCase();
                // Fusion : communes officielles (API, MAJUSCULES) d'abord, puis villes
                // déjà saisies non redondantes. Déduplication insensible à la casse.
                const internal = (villes || [])
                  .filter((v) => v && (!q || (v.toLowerCase().includes(q) && v.toLowerCase() !== q)));
                const seen = new Set();
                const list = [];
                for (const v of [...(apiVilles || []), ...internal]) {
                  const up = String(v).toUpperCase();
                  if (!seen.has(up)) { seen.add(up); list.push(up); }
                  if (list.length >= 8) break;
                }
                if (list.length === 0) return null;
                return (
                  <div className="absolute left-0 right-0 top-[calc(100%+2px)] z-50 max-h-40 overflow-y-auto rounded-lg border border-[var(--border-soft)] bg-white p-1 shadow-[var(--shadow-md)]" data-testid={`pap-cell-ville-suggestions-${ligne.id}-${date}`}>
                    {list.map((v) => (
                      <button key={v} type="button"
                        onMouseDown={(e) => { e.preventDefault(); setVille(v); setVilleBoxOpen(false); }}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[rgba(238,114,46,0.08)]"
                        data-testid={`pap-cell-ville-option-${v}`}>
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
                        <span className="truncate font-semibold">{v}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Zone</Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger className="mt-1 h-9" data-testid={`pap-cell-zone-select-${ligne.id}-${date}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ZONE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1 h-9" data-testid={`pap-cell-status-${ligne.id}-${date}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Extension multi-jours : cocher les jours où appliquer la même ville */}
          <div>
            <Label className="flex items-center gap-1 text-[11px]"><CalendarRange className="h-3 w-3 text-[var(--shariti-orange)]" /> Appliquer sur les jours</Label>
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`pap-cell-days-${ligne.id}-${date}`}>
              {days.map((d) => {
                const on = selectedDays.includes(d.date);
                return (
                  <button key={d.date} type="button" onClick={() => toggleDay(d.date)}
                    data-testid={`pap-cell-day-${ligne.id}-${d.date}`}
                    className={`rounded-md border px-1.5 py-1 text-[10px] font-bold capitalize transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:border-[var(--shariti-orange)]"}`}>
                    {d.label.slice(0, 3)} {frenchDateShort(d.date).split("/")[0]}
                  </button>
                );
              })}
            </div>
            {selectedDays.length > 1 && (
              <p className="mt-1 text-[10px] font-semibold text-[var(--shariti-orange)]">La ville sera appliquée sur {selectedDays.length} jours.</p>
            )}
          </div>
          <div>
            <Label className="text-[11px]">Note (facultatif)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Consigne, secteur…" className="mt-1 min-h-[44px] text-sm" data-testid={`pap-cell-notes-${ligne.id}-${date}`} />
          </div>
          {/* Zone terrain délimitée sur carte (le responsable encadre le secteur). */}
          <div>
            {cell?.id ? (
              <Button type="button" variant="outline" onClick={() => setZoneOpen(true)}
                className={`h-9 w-full border-[var(--border-soft)] ${cell.geo_zone ? "text-[var(--shariti-orange)]" : ""}`}
                data-testid={`pap-cell-zone-map-${ligne.id}-${date}`}>
                <MapPin className="mr-1.5 h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                {cell.geo_zone ? "Modifier la zone terrain (carte)" : "Délimiter la zone terrain (carte)"}
              </Button>
            ) : (
              <p className="rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-1.5 text-[11px] text-muted-ink">
                <MapPin className="mr-1 inline h-3 w-3 text-[var(--shariti-orange)]" />
                Enregistrez d'abord la ville pour pouvoir délimiter la zone terrain sur la carte.
              </p>
            )}
          </div>
          {/* Équipe du jour (pointage terrain) — recruteurs + horaires par ville/jour. */}
          {cell?.id && (
            <Button type="button" variant="outline" onClick={() => { onTeam && onTeam(cell); setOpen(false); }}
              className="h-9 w-full border-[var(--border-soft)]" data-testid={`pap-cell-team-${ligne.id}-${date}`}>
              <Users className="mr-1.5 h-3.5 w-3.5 text-[var(--shariti-orange)]" />
              Équipe du jour{teamCount ? ` (${teamCount})` : ""}
            </Button>
          )}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            {cell ? (
              <Button variant="ghost" onClick={clear} disabled={saving}
                className="h-8 text-[#D64545] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#9B2226]" data-testid={`pap-cell-clear-${ligne.id}-${date}`}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Vider
              </Button>
            ) : <span />}
            <Button onClick={submit} disabled={saving || !ville.trim()}
              className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid={`pap-cell-save-${ligne.id}-${date}`}>
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Enregistrer
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
      {zoneOpen && (
        <ZoneMapDialog open={zoneOpen} onClose={() => setZoneOpen(false)}
          cell={cell} ligne={ligne} ville={cell?.ville}
          onSaved={() => onReload?.()} />
      )}
    </>
  );
}

/** Dialog de création / édition d'une ligne PAP (format sites privés). */
function LigneFormDialog({ open, onClose, initial, reference, onDone, category = "pap", formTitle = "ligne porte-à-porte" }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState("");
  const [assocId, setAssocId] = useState("");
  const [region, setRegion] = useState("");
  const [equipe, setEquipe] = useState("");
  const [responsable, setResponsable] = useState("");
  const [responsableId, setResponsableId] = useState("");
  const [respList, setRespList] = useState([]);
  const [newRespOpen, setNewRespOpen] = useState(false);
  const [newRespName, setNewRespName] = useState("");
  const [creatingResp, setCreatingResp] = useState(false);
  const [codeManuel, setCodeManuel] = useState("");
  const [color, setColor] = useState("#EE722E");
  const [saving, setSaving] = useState(false);

  const loadResp = useCallback(async () => {
    try { const { data } = await api.get("/pap/responsables"); setRespList(data || []); }
    catch { /* liste indisponible — on garde le champ vide */ }
  }, []);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setAssocId(initial?.association_id || "");
      setRegion(initial?.region_label || "");
      setEquipe(initial?.equipe_nom || "");
      setResponsable(initial?.responsable_nom || "");
      setResponsableId(initial?.responsable_id || "");
      setCodeManuel(initial?.code_campagne_manuel || "");
      setColor(initial?.color || "#EE722E");
      setNewRespOpen(false); setNewRespName("");
      loadResp();
    }
  }, [open, initial, loadResp]);

  const pickResp = (rid) => {
    setResponsableId(rid);
    const r = respList.find((x) => x.id === rid);
    if (r) {
      setResponsable(r.nom);
      if (r.color) setColor(r.color);
      if (!name.trim()) setName(r.nom);
    }
  };

  const createResp = async () => {
    const nm = newRespName.trim();
    if (!nm) { toast.error("Nom du responsable requis."); return; }
    setCreatingResp(true);
    try {
      const { data } = await api.post("/pap/responsables", { nom: nm, color });
      await loadResp();
      setResponsableId(data.id);
      setResponsable(data.nom);
      if (!name.trim()) setName(data.nom);
      setNewRespOpen(false); setNewRespName("");
      toast.success("Responsable créé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la création");
    } finally { setCreatingResp(false); }
  };

  const associations = reference?.associations || [];
  const selectedAssoc = associations.find((a) => a.id === assocId);
  const regions = selectedAssoc ? (reference?.regions_by_acronym?.[selectedAssoc.acronym] || []) : [];

  const submit = async () => {
    if (!name.trim()) { toast.error("Le nom du chef d'équipe est requis."); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      association_id: assocId || null,
      region_label: region || null,
      equipe_nom: equipe.trim() || null,
      responsable_nom: responsable.trim() || null,
      responsable_id: responsableId || null,
      code_campagne_manuel: codeManuel.trim() || null,
      color,
    };
    try {
      if (isEdit) await api.put(`/pap/lignes/${initial.id}`, payload);
      else await api.post("/pap/lignes", { ...payload, category });
      toast.success(isEdit ? "Ligne mise à jour" : "Ligne créée");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pap-ligne-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DoorOpen className="h-5 w-5 text-[var(--shariti-orange)]" />
            {isEdit ? `Modifier la ${formTitle}` : `Nouvelle ${formTitle}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Association</Label>
            <Select value={assocId} onValueChange={(v) => { setAssocId(v); setRegion(""); }}>
              <SelectTrigger className="mt-1" data-testid="pap-form-assoc"><SelectValue placeholder="Choisir une association…" /></SelectTrigger>
              <SelectContent>
                {associations.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} ({a.acronym})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Région / lieu</Label>
            <Select value={region} onValueChange={setRegion} disabled={!assocId || regions.length === 0}>
              <SelectTrigger className="mt-1" data-testid="pap-form-region">
                <SelectValue placeholder={assocId ? (regions.length ? "Choisir la région…" : "Aucune région PAP pour cette asso") : "Choisir d'abord l'association"} />
              </SelectTrigger>
              <SelectContent>
                {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Responsable d'équipe (liste PAP)</Label>
            <div className="mt-1 flex items-center gap-2">
              <Select value={responsableId || "__none__"} onValueChange={(v) => (v === "__none__" ? (setResponsableId(""), setResponsable("")) : pickResp(v))}>
                <SelectTrigger className="flex-1" data-testid="pap-form-responsable"><SelectValue placeholder="Choisir un responsable…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Aucun —</SelectItem>
                  {respList.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color || "#EE722E" }} />
                        {r.nom}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewRespOpen((v) => !v)}
                className="shrink-0" data-testid="pap-form-responsable-new-toggle" title="Nouveau responsable">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {newRespOpen && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-2" data-testid="pap-form-responsable-new">
                <Input value={newRespName} onChange={(e) => setNewRespName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createResp(); } }}
                  placeholder="Nom du nouveau responsable…" className="h-9 flex-1" data-testid="pap-form-responsable-new-input" autoFocus />
                <Button type="button" size="sm" onClick={createResp} disabled={creatingResp || !newRespName.trim()}
                  className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="pap-form-responsable-new-save">
                  {creatingResp ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ajouter"}
                </Button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Nom de l'équipe</Label>
            <Input value={equipe} onChange={(e) => setEquipe(e.target.value)} placeholder="ex : WARRIORS" className="mt-1" data-testid="pap-form-equipe" />
          </div>
          <div>
            <Label className="text-xs">Chef d'équipe (pastille)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Jimmy" className="mt-1" data-testid="pap-form-chef" />
          </div>
          <div>
            <Label className="text-xs">Couleur de la pastille</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="pap-form-colors">
              {CHEF_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`h-6 w-6 rounded-full border-2 transition-transform ${color === c ? "scale-110 border-ink" : "border-transparent"}`}
                  style={{ background: c }} title={c} data-testid={`pap-form-color-${c}`} />
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Code campagne (facultatif)</Label>
            <Input value={codeManuel} onChange={(e) => setCodeManuel(e.target.value)} placeholder="Laissez vide pour l'attribution automatique" className="mt-1" data-testid="pap-form-code" />
            <p className="mt-1 text-[11px] text-muted-ink">Vide = attribué automatiquement selon l'association, la région et le mois affiché.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !name.trim()}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="pap-form-submit">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />} {isEdit ? "Enregistrer" : "Créer la ligne"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Configuration des 3 tableaux de planning terrain (indépendants).
const BOARDS = {
  pap: {
    title: "Planning porte-à-porte",
    subtitle: "Une rangée par équipe • saisissez une ville (avec zone) par jour • étendez une ville sur plusieurs jours en un clic.",
    addLabel: "Ajouter une ligne PAP",
    formTitle: "ligne porte-à-porte",
  },
  rue_itinerance: {
    title: "Planning rue — itinérance",
    subtitle: "Villes hors Île-de-France sillonnées par les équipes (souvent 2 à 3 jours dans la même ville).",
    addLabel: "Ajouter une ligne itinérance",
    formTitle: "ligne rue — itinérance",
  },
  rue_fixe: {
    title: "Planning rue — fixe",
    subtitle: "Missions en rue où les équipes résident dans la ville concernée (Paris & Île-de-France).",
    addLabel: "Ajouter une ligne fixe",
    formTitle: "ligne rue — fixe",
  },
};

export default function PlanningPAP({ category = "pap" }) {
  const board = BOARDS[category] || BOARDS.pap;
  const { user } = useAuth();
  // Lecture seule : consultation par défaut, SAUF si « gestion complète » est
  // accordée pour ce tableau précis (permissions.board_<cat>_edit).
  const readOnly = user?.role === "lecture_seule" && !((user?.permissions || {})[`board_${category}_edit`]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [monday, setMonday] = useState(() => {
    const wk = searchParams.get("week");
    return wk && /^\d{4}-\d{2}-\d{2}$/.test(wk) ? mondayOf(wk) : mondayOf(new Date());
  });
  const [data, setData] = useState(null);
  const [reference, setReference] = useState(null);
  const [villes, setVilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSunday, setShowSunday] = useState(false);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null); // null=create, object=edit
  const [deleting, setDeleting] = useState(null); // ligne
  const dragRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [teamCell, setTeamCell] = useState(null);       // cellule PAP dont on ouvre l'équipe du jour
  const [teamCounts, setTeamCounts] = useState({});     // pap_cell_id -> nb d'affectations
  // --- UX mobile : bascule Jour/Semaine (< md). Défaut « Jour » (1 colonne large). ---
  const isMobile = useIsMobile();
  const isCompact = useIsMobile(1024);
  const [weekMode, setWeekMode] = useState("jour");
  const [dayIndex, setDayIndex] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pap/planning", { params: { week: monday, category } });
      setData(data);
    } finally { setLoading(false); }
  }, [monday, category]);

  const softLoad = useCallback(async () => {
    try {
      const { data } = await api.get("/pap/planning", { params: { week: monday, category } });
      setData(data);
    } catch { /* silencieux */ }
  }, [monday, category]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/pap/reference", { params: { category } }).then((r) => setReference(r.data)).catch(() => {}); }, [category]);
  const loadVilles = useCallback(() => {
    api.get("/pap/villes", { params: { category } }).then((r) => setVilles((r.data || []).map((v) => v.ville).filter(Boolean))).catch(() => {});
  }, [category]);
  useEffect(() => { loadVilles(); }, [loadVilles]);

  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set("week", monday);
    setSearchParams(sp, { replace: true });
  }, [monday]); // eslint-disable-line

  const allDays = data?.days || [];
  const days = showSunday ? allDays : allDays.slice(0, 6);
  // --- Vue mobile « Jour » : une seule journée à la fois (colonne large, lisible). ---
  const singleDayMode = isMobile && weekMode === "jour" && days.length > 0;
  const _todayISO = new Date().toLocaleDateString("en-CA");
  const _autoIdx = Math.max(0, days.findIndex((d) => String(d.date).slice(0, 10) === _todayISO));
  const selectedDayIdx = days.length === 0 ? 0 : Math.min(days.length - 1, dayIndex == null ? _autoIdx : dayIndex);
  const gridDays = singleDayMode ? [days[selectedDayIdx]] : days;
  const gridMinWidth = singleDayMode ? undefined : (isCompact ? `${180 + Math.max(gridDays.length, 1) * 162}px` : undefined);
  const lignes = data?.lignes || [];
  const weekHolidays = days.filter((d) => d.is_holiday);

  const cellsByKey = useMemo(() => {
    const map = {};
    (data?.cells || []).forEach((c) => { map[`${c.ligne_id}__${String(c.date).slice(0, 10)}`] = c; });
    return map;
  }, [data]);

  // Compteurs « Équipe (N) » par cellule PAP (badges du planning).
  const refreshTeamCounts = useCallback(async (cells) => {
    const ids = (cells || data?.cells || []).map((c) => c.id).filter(Boolean);
    if (!ids.length) { setTeamCounts({}); return; }
    try {
      const { data: counts } = await api.post("/pointage/affectations/cell-counts", { cell_ids: ids });
      setTeamCounts(counts || {});
    } catch { /* silencieux */ }
  }, [data]);

  useEffect(() => { refreshTeamCounts(data?.cells); }, [data?.cells, refreshTeamCounts]);

  // UX mobile : recentre le chip du jour sélectionné dans sa barre (scopé, sans
  // scrollIntoView qui décalerait les conteneurs parents).
  useEffect(() => {
    if (!(isMobile && weekMode === "jour")) return undefined;
    const id = requestAnimationFrame(() => {
      const container = document.querySelector('[data-testid="pap-day-chips"]');
      const el = container && container.querySelector('[data-selected="true"]');
      if (container && el) {
        const target = el.offsetLeft - (container.clientWidth - el.offsetWidth) / 2;
        container.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isMobile, weekMode, dayIndex, data]);

  const gridStyle = {
    gridTemplateColumns: singleDayMode
      ? `minmax(150px, 170px) minmax(0, 1fr)`
      : isCompact
        ? `minmax(180px, 180px) repeat(${Math.max(gridDays.length, 1)}, minmax(160px, 1fr))`
        : `minmax(210px, 260px) repeat(${Math.max(gridDays.length, 1)}, minmax(0, 1fr))`,
  };

  const openCreate = () => { setFormInitial(null); setFormOpen(true); };
  const openEdit = (ligne) => { setFormInitial(ligne); setFormOpen(true); };

  const deleteLigne = async () => {
    if (!deleting) return;
    try {
      const { data: res } = await api.delete(`/pap/lignes/${deleting.id}`);
      toast.success(`Ligne supprimée${res.cells_deleted ? ` (${res.cells_deleted} ville(s) retirée(s))` : ""}`);
      setDeleting(null);
      softLoad();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const persistOrder = async (arr) => {
    try { await api.post("/pap/lignes/reorder", { ordered_ids: arr.map((l) => l.id), category }); }
    catch { toast.error("Échec de la réorganisation"); }
  };
  const reorderLignes = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setData((d) => {
      if (!d) return d;
      const arr = [...(d.lignes || [])];
      const from = arr.findIndex((l) => l.id === sourceId);
      const to = arr.findIndex((l) => l.id === targetId);
      if (from < 0 || to < 0) return d;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      persistOrder(arr);
      return { ...d, lignes: arr };
    });
  };

  const saveCell = async (payload) => {
    try {
      await api.post("/pap/cell", { ...payload, category });
      toast.success(payload.dates && payload.dates.length > 1 ? `Ville appliquée sur ${payload.dates.length} jours` : "Ville enregistrée");
      softLoad();
      loadVilles();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const clearCell = async (cell) => {
    try {
      await api.delete(`/pap/cell/${cell.id}`);
      toast.success("Cellule vidée");
      softLoad();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const logoUrl = (id) => `${api.defaults.baseURL}/assets/association-logo/${id}`;

  return (
    <>
      <PageHeader title={board.title}
        subtitle={board.subtitle}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {readOnly && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-bold text-muted-ink" data-testid="pap-readonly-badge">
                <Eye className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Lecture seule
              </span>
            )}
            <Button variant="secondary" onClick={() => setShowSunday((o) => !o)} data-testid="pap-toggle-sunday"
              className={`whitespace-nowrap border ${showSunday ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white"}`}>
              <CalendarDays className="mr-2 h-4 w-4 shrink-0" /><span className="sm:hidden">{showSunday ? "Masquer dim." : "Dimanche"}</span><span className="hidden sm:inline">{showSunday ? "Masquer dimanche" : "Afficher le dimanche"}</span>
            </Button>
            {!readOnly && (
              <Button onClick={openCreate} data-testid="pap-add-ligne-btn"
                className="whitespace-nowrap bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
                <Plus className="mr-2 h-4 w-4 shrink-0" /><span className="sm:hidden">Ajouter</span><span className="hidden sm:inline">{board.addLabel}</span>
              </Button>
            )}
          </div>
        }
      >
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, -7))} data-testid="pap-week-prev" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronLeft className="h-4 w-4" /></Button>
          <Popover open={weekPickerOpen} onOpenChange={setWeekPickerOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="pap-week-jump-trigger"
                className="group rounded-lg border border-[var(--border-soft)] bg-white px-4 py-1.5 text-center transition-colors hover:border-[var(--shariti-orange)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
                <div className="text-[10px] font-semibold uppercase text-muted-ink">Semaine {data?.week_number}</div>
                <div className="flex items-center justify-center gap-1.5 text-sm font-bold" data-testid="pap-week-range">
                  {days[0] && frenchDate(days[0].date)} – {days[days.length - 1] && frenchDate(days[days.length - 1].date)}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-ink" />
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-72" data-testid="pap-week-jump-popover">
              <div className="space-y-2">
                <div className="text-xs font-bold text-ink">Aller à une semaine</div>
                <Input type="date" value={monday}
                  onChange={(e) => { if (e.target.value) { setMonday(mondayOf(e.target.value)); setWeekPickerOpen(false); } }}
                  className="h-9" data-testid="pap-week-jump-date" />
                <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="pap-week-jump-today"
                  onClick={() => { setMonday(mondayOf(new Date())); setWeekPickerOpen(false); }}>Cette semaine</Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, 7))} data-testid="pap-week-next" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        {weekHolidays.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-[rgba(240,190,20,0.55)] bg-[rgba(252,219,83,0.30)] px-3 py-1.5 text-xs font-semibold text-[#5A4200]" data-testid="pap-holiday-note">
            <Flag className="h-3.5 w-3.5 shrink-0 text-[#8A6D00]" />
            <span>Jour{weekHolidays.length > 1 ? "s" : ""} férié{weekHolidays.length > 1 ? "s" : ""} :</span>
            {weekHolidays.map((d) => (
              <span key={d.date} className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5">
                {d.holiday_name} <span className="font-normal text-[#8A6D00]">({frenchDateShort(d.date)})</span>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-white" />)}</div>
        ) : (
          <>
          {isMobile && (
            <div className="mb-3 space-y-2" data-testid="pap-mobile-controls">
              <div className="flex items-center justify-between gap-2">
                <ToggleGroup type="single" value={weekMode} onValueChange={(v) => v && setWeekMode(v)}
                  className="justify-start rounded-lg border border-[var(--border-soft)] bg-white p-0.5">
                  <ToggleGroupItem value="jour" className="h-9 rounded-md px-4 text-xs font-bold data-[state=on]:bg-[var(--shariti-orange)] data-[state=on]:text-white" data-testid="pap-view-jour">Jour</ToggleGroupItem>
                  <ToggleGroupItem value="semaine" className="h-9 rounded-md px-4 text-xs font-bold data-[state=on]:bg-[var(--shariti-orange)] data-[state=on]:text-white" data-testid="pap-view-semaine">Semaine</ToggleGroupItem>
                </ToggleGroup>
                {singleDayMode && (
                  <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted-ink" data-testid="pap-day-count">
                    {Number(gridDays[0]?.bulletins_total) > 0 && (
                      <span
                        data-testid="pap-day-bulletins-total"
                        title={`${gridDays[0].bulletins_total} bulletin(s) ce jour (total)`}
                        className="grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)]"
                        style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
                        {gridDays[0].bulletins_total}
                      </span>
                    )}
                    {gridDays[0]?.count ?? 0} équipe(s)
                  </span>
                )}
              </div>
              {singleDayMode && (
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5" data-testid="pap-day-chips">
                  {days.map((d, i) => (
                    <button key={d.date} type="button" onClick={() => setDayIndex(i)}
                      className={`flex shrink-0 items-center gap-1 rounded-full border px-3 py-2 text-xs font-bold capitalize transition-[background-color,border-color,color] ${i === selectedDayIdx ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink"} ${d.is_holiday ? "ring-1 ring-[rgba(252,219,83,0.7)]" : ""}`}
                      data-selected={i === selectedDayIdx ? "true" : undefined}
                      data-testid={`pap-day-chip-${i}`}>
                      {d.label.slice(0, 3)} <span className="font-normal opacity-80">{frenchDateShort(d.date)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="relative overflow-x-auto overscroll-x-contain rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] no-scrollbar lg:overflow-x-visible">
            <div className="lg:min-w-0" style={{ minWidth: gridMinWidth }}>
              {/* Header */}
              <div className="grid border-b border-[var(--border-soft)] bg-[var(--surface-2)]" style={gridStyle}>
                <div className={`sticky left-0 z-20 border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink ${isCompact ? "sticky-col-shadow" : ""}`}>Ligne</div>
                {gridDays.map((d, i) => (
                  <div key={d.date} className={`px-2 py-2 ${d.is_holiday ? "bg-[rgba(252,219,83,0.30)]" : d.is_sunday ? "bg-[rgba(238,114,46,0.05)]" : ""} ${i === 2 || i === 4 || d.is_sunday ? "border-l-2 border-[rgba(238,114,46,0.22)]" : "border-l border-[var(--border-soft)]"}`} data-testid={`pap-day-header-${i}`}>
                    <div className="flex items-center justify-between gap-1">
                      <div className="min-w-0 truncate text-xs font-bold capitalize">{d.label} <span className="font-normal text-muted-ink">{frenchDateShort(d.date)}</span></div>
                      <div className="flex shrink-0 items-center gap-1">
                        {Number(d.bulletins_total) > 0 && (
                          <span
                            data-testid={`pap-day-bulletins-total-${i}`}
                            title={`${d.bulletins_total} bulletin${d.bulletins_total > 1 ? "s" : ""} enregistré${d.bulletins_total > 1 ? "s" : ""} ce jour (total)`}
                            className="grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)] ring-1 ring-white/70"
                            style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
                            {d.bulletins_total}
                          </span>
                        )}
                        <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]" data-testid={`pap-daycount-${i}`} title={`${d.count} équipe(s)`}>{d.count}</span>
                      </div>
                    </div>
                    {d.is_holiday && (
                      <div className="mt-1 flex items-center gap-1 rounded-md border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.55)] px-1.5 py-0.5" title={`Jour férié : ${d.holiday_name}`}>
                        <Flag className="h-3 w-3 shrink-0 text-[#8A6D00]" />
                        <span className="truncate text-[10px] font-bold text-[#5A4200]">{d.holiday_name}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Empty state */}
              {lignes.length === 0 && (
                <div className="flex flex-col items-center justify-center px-6 py-14 text-center" data-testid="pap-empty">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--surface-2)]">
                    <DoorOpen className="h-6 w-6 text-[var(--shariti-orange)]" />
                  </div>
                  <div className="mt-3 text-sm font-bold">Aucune {board.formTitle}</div>
                  {readOnly ? (
                    <p className="mt-1 max-w-md text-xs text-muted-ink">Aucune ligne n'est encore disponible sur ce planning.</p>
                  ) : (
                    <>
                      <p className="mt-1 max-w-md text-xs text-muted-ink">Créez une ligne par équipe : association, région, nom d'équipe, responsable et chef d'équipe. Vous pourrez ensuite y saisir des villes jour par jour.</p>
                      <Button onClick={openCreate} data-testid="pap-empty-add"
                        className="mt-3 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
                        <Plus className="mr-2 h-4 w-4" />{board.addLabel}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Rows */}
              {lignes.map((ligne) => {
                const eq = (ligne.equipe_nom || "").toUpperCase();
                const resp = (ligne.responsable_nom || "").toUpperCase();
                const equipeLabel = [eq, resp].filter(Boolean).join(" - ");
                return (
                  <div key={ligne.id} className="grid border-b border-[var(--border-soft)] last:border-0" style={gridStyle} data-testid={`pap-ligne-row-${ligne.id}`}>
                    <div
                      className={`relative sticky left-0 z-10 border-r border-[var(--border-soft)] bg-white px-3 py-2.5 transition-shadow ${isCompact ? "sticky-col-shadow" : ""} ${dragOverId === ligne.id ? "shadow-[inset_0_3px_0_0_var(--shariti-orange)]" : ""}`}
                      onDragOver={(e) => { if (dragRef.current) { e.preventDefault(); if (dragOverId !== ligne.id) setDragOverId(ligne.id); } }}
                      onDragLeave={() => { if (dragRef.current) setDragOverId((cur) => (cur === ligne.id ? null : cur)); }}
                      onDrop={(e) => { if (dragRef.current) { e.preventDefault(); reorderLignes(dragRef.current, ligne.id); dragRef.current = null; setDragOverId(null); } }}
                      data-testid={`pap-ligne-header-${ligne.id}`}
                    >
                      {/* Menu actions */}
                      {!readOnly && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" onMouseDown={(e) => e.stopPropagation()}
                              className="absolute right-1 top-1 z-20 grid h-6 w-6 place-items-center rounded-md text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
                              title="Actions sur la ligne" data-testid={`pap-ligne-menu-${ligne.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48" data-testid={`pap-ligne-menu-content-${ligne.id}`}>
                            <DropdownMenuLabel className="truncate">{ligne.equipe_nom || ligne.name}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openEdit(ligne)} data-testid={`pap-ligne-edit-${ligne.id}`}>
                              <Pencil className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Modifier la ligne
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeleting(ligne)} className="text-[#D64545] focus:text-[#D64545]" data-testid={`pap-ligne-delete-${ligne.id}`}>
                              <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {/* Ligne 1 : poignée + semaine + badge PAP */}
                      <div className="flex flex-wrap items-center gap-1 text-[10px] font-semibold text-muted-ink">
                        {!readOnly && (
                          <span draggable
                            onDragStart={(e) => { dragRef.current = ligne.id; e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", ligne.id); } catch (_) {} }}
                            onDragEnd={() => { dragRef.current = null; setDragOverId(null); }}
                            className="-ml-1 cursor-grab rounded p-0.5 text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink active:cursor-grabbing"
                            title="Glisser pour réorganiser" data-testid={`pap-ligne-drag-${ligne.id}`}>
                            <GripVertical className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5">S{data?.week_number}</span>
                      </div>

                      {/* Ligne 2 : logo asso + canal PAP + code campagne */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 leading-snug">
                        {ligne.assoc?.has_logo ? (
                          <img src={logoUrl(ligne.assoc.id)} alt={ligne.assoc?.acronym || "association"} title={ligne.assoc?.name || ""}
                            className="h-8 w-auto max-w-[76px] shrink-0 object-contain" data-testid={`pap-ligne-logo-${ligne.id}`} />
                        ) : ligne.assoc ? (
                          <span className="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-extrabold text-white"
                            title={ligne.assoc?.name || ""} style={{ background: ligne.assoc?.color || "#EE722E" }}>
                            {ligne.assoc?.acronym || "—"}
                          </span>
                        ) : null}
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[10px] font-bold text-muted-ink" title="Canal de recrutement">
                          <DoorOpen className="h-3 w-3" /> {ligne.canal_nom || "Porte-à-porte"}
                        </span>
                        {ligne.code_campagne && (
                          <span className="inline-flex shrink-0 items-center rounded bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-[var(--shariti-orange)]"
                            title="Code campagne" data-testid={`pap-ligne-code-${ligne.id}`}>
                            {ligne.code_campagne}
                          </span>
                        )}
                      </div>

                      {/* Ligne 3 : lieu / région */}
                      {ligne.lieu && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-muted-ink" title="Lieu / région" data-testid={`pap-ligne-lieu-${ligne.id}`}>
                          <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{ligne.lieu}</span>
                        </div>
                      )}

                      {/* Ligne 4 : ÉQUIPE - RESPONSABLE */}
                      {equipeLabel && (
                        <div className="mt-1 truncate text-[11px] font-bold text-ink" data-testid={`pap-ligne-equipe-${ligne.id}`} title={equipeLabel}>
                          {equipeLabel}
                        </div>
                      )}

                      {/* Ligne 5 : pastille chef d'équipe (équivalent CDP) */}
                      {ligne.name && (
                        <div className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-ink" data-testid={`pap-ligne-chef-${ligne.id}`}>
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: ligne.color || "#EE722E" }} />
                          <span className="truncate" title={`Chef d'équipe : ${ligne.name}`}>{ligne.name}</span>
                        </div>
                      )}

                      {/* Ligne 6 : traçabilité (créée / modifiée par, et quand) */}
                      {(ligne.created_by || ligne.updated_by || ligne.created_at) && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button"
                              className="mt-1.5 inline-flex max-w-full items-center gap-1 rounded text-[9px] font-medium text-muted-ink/80 transition-colors hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--shariti-orange)]"
                              title="Voir la traçabilité de la ligne" data-testid={`pap-ligne-trace-${ligne.id}`}>
                              <History className="h-2.5 w-2.5 shrink-0" />
                              <span className="truncate">
                                {ligne.updated_by ? `Modifié par ${ligne.updated_by}` : `Créé par ${ligne.created_by || "—"}`}
                              </span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-64 p-3" data-testid={`pap-ligne-trace-content-${ligne.id}`}>
                            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-ink">
                              <History className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Traçabilité de la ligne
                            </div>
                            <div className="space-y-2 text-[11px]">
                              <div className="flex items-start gap-1.5">
                                <Plus className="mt-0.5 h-3 w-3 shrink-0 text-muted-ink" />
                                <div className="min-w-0">
                                  <div><span className="font-semibold text-ink">Créée</span> par {ligne.created_by || "—"}</div>
                                  <div className="text-muted-ink">{fmtDateTime(ligne.created_at)}</div>
                                </div>
                              </div>
                              {ligne.updated_by ? (
                                <div className="flex items-start gap-1.5 border-t border-[var(--border-soft)] pt-2">
                                  <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                                  <div className="min-w-0">
                                    <div><span className="font-semibold text-ink">Modifiée</span> par {ligne.updated_by}</div>
                                    <div className="text-muted-ink">{fmtDateTime(ligne.updated_at)}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="border-t border-[var(--border-soft)] pt-2 text-muted-ink">Aucune modification depuis la création.</div>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    {gridDays.map((d, i) => {
                      const cell = cellsByKey[`${ligne.id}__${d.date}`];
                      return (
                        <div key={d.date}
                          className={`p-1 ${d.is_holiday ? "bg-[rgba(252,219,83,0.10)]" : d.is_sunday ? "bg-[rgba(238,114,46,0.03)]" : ""} ${i === 2 || i === 4 || d.is_sunday ? "border-l-2 border-[rgba(238,114,46,0.14)]" : "border-l border-[var(--border-soft)]"}`}>
                          <PapCellEditor ligne={ligne} date={d.date} cell={cell} days={days} villes={villes} onSave={saveCell} onClear={clearCell} onReload={load} onTeam={(c) => setTeamCell({ ...c, _ligne: ligne })} teamCount={cell ? (teamCounts[cell.id] || 0) : 0} readOnly={readOnly} />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          </>
        )}
      </div>

      {/* Créer / modifier une ligne */}
      <LigneFormDialog open={formOpen} onClose={() => setFormOpen(false)} initial={formInitial}
        reference={reference} category={category} formTitle={board.formTitle}
        onDone={() => { setFormOpen(false); softLoad(); }} />

      {/* Équipe du jour (pointage terrain PAP) — panneau latéral d'affectation rapide */}
      <TeamSheet
        target={teamCell ? {
          kind: "pap", id: teamCell.id, date: teamCell.date,
          name: `${(teamCell.ville || "Ville")}${teamCell.zone ? ` (ZONE ${teamCell.zone})` : ""}`,
          address: teamCell.ville,
        } : null}
        weekDays={days.map((d) => d.date)}
        onClose={() => setTeamCell(null)}
        onChanged={() => refreshTeamCounts(data?.cells)}
        canEdit={!readOnly}
      />

      {/* Supprimer */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="pap-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-[#D64545]" /> Supprimer la ligne PAP</AlertDialogTitle>
            <AlertDialogDescription>
              Supprimer définitivement la ligne « {deleting?.equipe_nom ? `${deleting.equipe_nom} - ` : ""}{deleting?.name} » et toutes ses villes saisies ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pap-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLigne} className="bg-[#D64545] text-white hover:bg-[#B83A3A]" data-testid="pap-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
